<template>
  <div class="campaigns-view">
    <app-view-layout
      header="Kampanie"
      fluid
    >
      <template #toolbarMenu>
        <v-btn
          v-if="actionsPermitted"
          color="primary"
          @click="openCampaignDialog()"
        >
          Dodaj kampanię
        </v-btn>
      </template>

      <template #content>
        <AppSearchInput
          class="template-search mb-4"
          search-placeholder="Wyszukaj kampanii"
          @search="searchQuery = $event"
        />

        <v-data-table
          class="view-table"
          :headers="permittedHeaders"
          :items="campaignsList"
          :items-per-page="12"
          fixed-header
          show-expand
          single-expand
          :search="searchQuery"
          no-result-text="Brak pasujących kampanii"
          :loading="isProcessing"
          loading-text="Wyszukiwanie kampanii"
          :footer-props="{
            itemsPerPageOptions: [12,24,36],
            itemsPerPageText: 'Pozycji na stronę',
          }"
        >
          <template #no-results>
            <AppEmptyList class="projects-index__empty py-10" />
          </template>

          <template #[`item.projects`]="{item}">
            <AppLimitedItems
              :items="excludeDeletedProjects(item.projects)"
              :visible-items="2"
            />
          </template>

          <template #[`item.timespan`]="{item}">
            {{ formatDateOutput([item.startDate, item.endDate], true) }}
          </template>

          <template #[`item.actions`]="{item}">
            <v-btn
              icon
              color="error"
              class="mr-2"
              @click="confirmDeleteCampaign(item)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
            <v-btn
              icon
              color="primary"
              @click="openCampaignDialog(item)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </template>

          <template #expanded-item="{ headers, item }">
            <td
              :colspan="headers.length"
              class="px-0"
            >
              <v-data-table
                class="expanded-table"
                :headers="staticData.expandedHeaders"
                :items="item.projects"
                :items-per-page="-1"
                fixed-header
                no-data-text="Brak aktywnych projektów"
                hide-default-footer
              >
                <template #item="{ item:project }">
                  <ProjectsTableRow
                    :project="project"
                    campaign-view
                  />
                </template>
              </v-data-table>
            </td>
          </template>
        </v-data-table>
      </template>
    </app-view-layout>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import AppViewLayout from '@/components/Layout/AppViewLayout.vue';
import ProjectCard from '@/components/Projects/ProjectCard.vue';
import AppEmptyList from '@/components/UI/AppEmptyList.vue';
import AppStatus from '@/components/UI/AppStatus.vue';
import AppSearchInput from '@/components/UI/AppSearchInput.vue';
import AppMultiselectChoices from '@/components/UI/AppMultiselectChoices.vue';
import AppLimitedItems from '@/components/UI/AppLimitedItems.vue';
import ProjectsTableRow from '@/components/Projects/ProjectsTableRow.vue';
import utils from '@/helpers/utils';

const headers = [
  { text: 'Nazwa kampanii', value: 'name' },
  { text: 'Czas realizacji', value: 'timespan' },
  { text: 'Projekty w kampanii', value: 'projects' },
  { text: '', value: 'actions', width: 110 },
];

const expandedHeaders = [
  { text: '', value: 'avatar', sortable: false },
  { text: 'Nazwa projektu', value: 'name', sortable: false },
  { text: 'ID projektu', value: 'id', sortable: false },
  { text: 'Czas realizacji', value: 'projectPeriod', sortable: false },
  { text: 'Typ projektu', value: 'projectType', sortable: false },
  { text: 'Zespół projektowy', value: 'members', sortable: false },
  { text: 'Zespół zlecający', value: 'orderers', sortable: false },
  { text: 'Status', value: 'status', sortable: false },
];

export default {
  components: {
    AppViewLayout,
    ProjectCard,
    AppEmptyList,
    AppStatus,
    AppSearchInput,
    AppMultiselectChoices,
    AppLimitedItems,
    ProjectsTableRow,
  },

  data: () => ({
    searchQuery: '',
    staticData: {
      expandedHeaders,
      utils,
    },
  }),

  computed: {
    ...mapState('campaigns', [
      'campaignsList',
      'isProcessing',
    ]),
    ...mapGetters('auth', [
      'checkUserPermissions',
    ]),

    actionsPermitted() {
      return this.checkUserPermissions(['admin_director', 'chief']);
    },

    permittedHeaders() {
      return this.actionsPermitted ? headers : headers.filter((header) => header.value !== 'actions');
    },
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.fetchCampaigns();
    });
  },

  methods: {
    ...mapActions('campaigns', [
      'fetchCampaigns',
      'deleteCampaign',
    ]),

    ...mapActions('dialog', [
      'showDialog',
      'clearDialog',
    ]),

    formatDateOutput: utils.formatDateOutput,

    excludeDeletedProjects(projects) {
      return projects.filter((project) => !project.softDeleted);
    },

    openCampaignDialog(campaign) {
      this.showDialog({
        header: campaign ? 'Edytuj kampanię' : 'Stwórz kampanię',
        component: 'CampaignForm',
        props: {
          campaign,
        },
      });
    },

    confirmDeleteCampaign(campaign) {
      this.showDialog({
        header: 'Usuwanie kampanii',
        text: 'Czy na pewno chcesz usunąć kampanię?',
        component: 'Confirm',
        props: {
          confirmText: 'Usuń',
          rejectText: 'Nie usuwaj',
        },
      })
      .then(() => {
        this.deleteCampaign(campaign);
      })
      .catch(() => {
        this.clearDialog();
      });
    },

    getProjects(projects, end = projects.length, start = 0) {
      return projects.map((project) => utils.textExcerpt(project.name, 40)).slice(start, end).join(', ');
    },
  },
};
</script>

<style lang="scss" scoped>
.campaigns-view {
  &__empty {
    min-height: calc(100vh - 220px);
  }
  .view-table::v-deep  .v-data-table__wrapper {
    max-height: calc(100vh - 300px);
  }
}
</style>
